import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { ArrowRight } from "lucide-react";

/**
 * Reusable "coming soon" hub page used by Academy, Permit Finder and Marketing.
 * Renders a header, intro copy, three feature cards and CTAs. All instances
 * share the orange brand color so the visual identity stays consistent.
 */
export default function InfoPage({
  eyebrow,
  title,
  description,
  features = [],
  ctaPrimary = { label: "Start free", to: "/register" },
  ctaSecondary = { label: "Talk to us", to: "/#contact" },
  testid,
}) {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={testid}>
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="sm" withTagline />
          <Link
            to="/"
            className="text-sm font-medium text-slate-600 hover:text-[#E8650A] transition"
            data-testid="info-back-link"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-orange-50/70 via-white to-white" />
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
          <span
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#E8650A] bg-[#FDECE2] px-3 py-1 rounded-full"
            data-testid="info-eyebrow"
          >
            {eyebrow}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            {title}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to={ctaPrimary.to} data-testid="info-cta-primary">
              <Button size="lg" className="ff-btn-primary h-12 px-6 text-base font-semibold">
                {ctaPrimary.label}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <Link to={ctaSecondary.to} data-testid="info-cta-secondary">
              <Button size="lg" variant="outline" className="h-12 px-6 text-base font-semibold">
                {ctaSecondary.label}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {features.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-24">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-slate-200 bg-white p-6 hover:border-[#E8650A]/40 transition"
                data-testid={`info-feature-${f.testid}`}
              >
                <div className="h-10 w-10 rounded-lg bg-[#FDECE2] text-[#E8650A] grid place-items-center">
                  {f.icon}
                </div>
                <h3 className="mt-4 font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-[#E8650A]/20 bg-[#FDECE2]/40 px-6 py-8 sm:px-10 sm:py-10 text-center">
            <p className="text-xs font-bold uppercase tracking-wider text-[#E8650A]">Coming soon</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">
              {"We're building this for you next."}
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto">
              {"Drop your email and we'll let you know the moment it goes live. Early signups get free access."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              <Link to="/#contact" data-testid="info-notify-cta">
                <Button size="lg" className="ff-btn-primary h-11 px-5 font-semibold">
                  Notify me
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
